import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useTransitionStyles,
  useDismiss,
  useInteractions,
  useClick,
  FloatingFocusManager,
} from '@floating-ui/react'
import { observer } from 'mobx-react-lite'
import { useId, useState } from 'react'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { MoreHorizontalFill } from 'components/icons/MoreHorizontalFill'
import { useTranslationTyped } from 'i18n/i18n'

export const NotificationsActionButton = observer(
  function NotificationsActionButton({
    containerClassName,
    children,
  }: {
    containerClassName?: string
    children: React.ReactNode
  }) {
    const { tt } = useTranslationTyped()
    const [isOpen, setIsOpen] = useState(false)
    const menuId = useId()

    const { refs, floatingStyles, context } = useFloating({
      open: isOpen,
      onOpenChange: setIsOpen,
      placement: 'bottom-end',
      // Make sure the tooltip stays on the screen
      whileElementsMounted: autoUpdate,
      middleware: [
        offset(10),
        flip({
          fallbackAxisSideDirection: 'start',
        }),
        shift(),
      ],
    })

    const { isMounted, styles } = useTransitionStyles(context, {
      // Configure both open and close durations:
      duration: 300,
    })

    // Event listeners to change the open state
    const click = useClick(context)
    const dismiss = useDismiss(context)

    // Merge all the interactions into prop getters
    const { getReferenceProps, getFloatingProps } = useInteractions([
      click,
      dismiss,
    ])

    const component = isMounted ? (
      <FloatingFocusManager context={context} restoreFocus={true}>
        <div
          id={menuId}
          className={containerClassName}
          style={{ ...floatingStyles, ...styles }}
          ref={refs.setFloating}
          {...getFloatingProps()}
        >
          {children}
        </div>
      </FloatingFocusManager>
    ) : undefined

    return (
      <div
        className="relative"
        {...getReferenceProps()}
        ref={refs.setReference}
      >
        <BreakoutButton
          kind="secondary"
          size="small"
          aria-label={tt.notifications.notification_actions()}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-controls={menuId}
          icon={<MoreHorizontalFill aria-hidden="true" size={16} />}
          onClick={() => setIsOpen(!isOpen)}
        />

        {component}
      </div>
    )
  }
)
